import "../../../../index.css";
import React, { useEffect, useState, useMemo } from "react";
import {
  UserOutlined,
  BorderOutlined,
  CheckSquareOutlined,
} from "@ant-design/icons";
import {
  Button,
  Input,
  Modal,
  Divider,
  List,
  Typography,
  notification,
  Select,
  Form,
  InputNumber,
} from "antd";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { closeModal } from "store/slices/modalsSlice";
import { getStudents } from "store/slices/studentsSlice";
import {
  addStudentToTraining,
  getStudentsForTraining,
} from "store/slices/trainingsSlice";

const AddStudentsToTrainingModal = () => {
  const { t } = useTranslation();
  const {
    isModalOpen,
    modalType,
    modalProps: { trainingId, states },
  } = useSelector((state) => state?.modals);

  const [form] = Form?.useForm();
  const dispatch = useDispatch();
  const open = isModalOpen && modalType === "add_students_to_training";
  const { training, loading } = useSelector((state) => state?.trainings);
  const { students } = useSelector((state) => state?.students);
  useEffect(() => {
    if (open) {
      dispatch(getStudents());
    }
  }, [dispatch, trainingId, open]);

  const [isOpenSearch, setIsOpenSearch] = useState(false);
  const [searchInput, setSearchInput] = useState("");
  const [selectedStudents, setSelectedStudents] = useState([]);
  const allowedStates = Array?.isArray(states) ? states : [];

  useEffect(() => {
    const initialValues = {
      amount: {},
      currency: {},
      learningType: {},
    };

    for (const student of selectedStudents) {
      initialValues.amount[student._id] = student?.payment?.amount;
      initialValues.currency[student._id] = student?.payment?.currency;
      initialValues.learningType[student._id] =
        student?.learningType || allowedStates[0] || "";
    }

    form?.setFieldsValue(initialValues);
  }, [selectedStudents, form, allowedStates]);

  const filteredStudents = useMemo(
    () =>
      students?.filter(
        (student) =>
          student?.fullName?.toLowerCase()?.includes(searchInput?.toLowerCase()) &&
          !training?.students?.some(
            (studentFormation) => studentFormation?.studentId === student?._id
          )
      ),
    [students, searchInput, training?.students]
  );

  const handleSearchInput = (e) => {
    setSearchInput(e?.target?.value);
  };

  const handleSelection = (student) => {
    setSelectedStudents((prev) =>
      prev?.some(({ _id }) => _id === student?._id)
        ? prev?.filter(({ _id }) => _id !== student?._id)
        : [
            ...prev,
            {
              ...student,
              payment: { amount: 0, currency: "dzd" },
              learningType: allowedStates[0] || "",
            },
          ]
    );
  };

  const handlePayment = (e, student, field) => {
    const value =
      field === "amount" ? Number?.parseInt(e, 10) || 0 : e;

    setSelectedStudents((prevStudents) =>
      prevStudents?.map((s) =>
        s?._id === student?._id
          ? { ...s, payment: { ...s?.payment, [field]: value } }
          : s
      )
    );
  };

  const handleLearningTypeChange = (value, student) => {
    if (!allowedStates?.includes(value)) return;
    setSelectedStudents((prevStudents) =>
      prevStudents?.map((s) =>
        s?._id === student?._id ? { ...s, learningType: value } : s
      )
    );
  };
  const getLearningTypeValue = (student) =>
    student?.learningType || allowedStates[0] || "";

  const handleSubmit = async () => {
    if (selectedStudents?.length > 0) {
      const students = selectedStudents?.map((student) => ({
        studentId: student?._id,
        payment: student?.payment,
        learningType: student?.learningType,
      }));
      try {
        await dispatch(
          addStudentToTraining({
            trainingId,
            students,
          })
        )?.unwrap();

        notification?.success({
          message: t("students.crud.form.addToTraining.success"),
        });
        dispatch(closeModal());
        dispatch(getStudentsForTraining(trainingId));
        setSelectedStudents([]);
      } catch (error) {
        notification?.error({
          message: t("students.crud.form.addToTraining.failed"),
          description: error,
        });
      }
    } else {
      dispatch(closeModal());
    }
  };

  return (
    <>
      <Modal
        title={t("students.form.label.addToTraining.title")}
        open={open}
        centered
        onCancel={() => {
          dispatch(closeModal());
        }}
        onOk={() => {
          form?.validateFields()?.then(() => {
              handleSubmit();
            })?.catch((error_) => {
              notification?.error({
                message: t("common.error.generic"),
                description:
                  error_?.errorFields?.[0]?.errors?.[0] ||
                  t("common.error.generic"),
              });
            });
        }}
        confirmLoading={loading}
      >
        <Form form={form} layout="vertical">
          <Button
            onClick={() => {
              setIsOpenSearch(true);
            }}
          >
            {t("students.title")}
          </Button>

          <List
            header={
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center", // Align items vertically
                }}
              >
                <p style={{ flex: 2 }}>{t("students.table.column.fullName")}</p>
                <p style={{ flex: 1 }}>
                  {t("students.form.label.learningType")}
                </p>
                <p style={{ flex: 1 }}>{t("expenses.paymentMethod.payment")}</p>
              </div>
            }
            dataSource={selectedStudents}
            renderItem={(student) => (
              <List.Item>
                <Typography.Text style={{ flex: 2 }}>
                  {student?.fullName}
                </Typography.Text>
                <Form.Item
                  name={["learningType", student?._id]}
                  rules={[
                    {
                      required: true,
                      message: t("students.form.error.learningType.required"),
                    },
                  ]}
                >
                  <Select
                    // defaultValue="particulier"
                    style={{ width: 120, flex: 1 }}
                    value={getLearningTypeValue(student)}
                    onChange={(value) =>
                      handleLearningTypeChange(value, student)
                    }
                  >
                    {states?.includes("particulier") && (
                      <Select.Option value="particulier">
                        {t("students.form.option.particulier")}
                      </Select.Option>
                    )}
                    {states?.includes("miniGroupe") && (
                      <Select.Option value="miniGroupe">
                        {t("students.form.option.miniGroupe")}
                      </Select.Option>
                    )}
                    {states?.includes("groupe") && (
                      <Select.Option value="groupe">
                        {t("students.form.option.groupe")}
                      </Select.Option>
                    )}
                  </Select>
                  {/* <Select
                    // defaultValue="particulier"
                    style={{ width: 120, flex: 1 }}
                    value={getLearningTypeValue(student)}
                    onChange={(value) => handleLearningTypeChange(value, student)}
                  >
                    {states.includes("particulier") && student.learningType === "particulier" && (
                      <Select.Option value="particulier">
                        {t("students.form.option.particulier")}
                      </Select.Option>
                    )}
                    {states.includes("miniGroupe") && student.learningType === "miniGroupe" && (
                      <Select.Option value="miniGroupe">
                        {t("students.form.option.miniGroupe")}
                      </Select.Option>
                    )}
                    {states.includes("groupe") && student.learningType === "groupe" && (
                      <Select.Option value="groupe">
                        {t("students.form.option.groupe")}
                      </Select.Option>
                    )}
                  </Select> */}
                </Form.Item>
                <Typography.Text style={{ flex: 1 }}>
                  <Form.Item
                    name={["amount", student?._id]}
                    rules={[
                      {
                        required: true,
                        message: t("students.form.error.amount.required"),
                      },
                      {
                        type: "number",
                        min: 0,
                        message: t("students.form.error.amount.min"),
                      },
                    ]}
                  >
                    <InputNumber
                      type="number"
                      value={student?.payment?.amount}
                      controls={false}
                      onChange={(e) => {
                        handlePayment(e, student, "amount");
                        form?.setFieldValue(
                          ["amount", student?._id],
                          Number(e)
                        );
                      }}
                    />
                  </Form.Item>
                  <Form.Item
                    name={["currency", student?._id]}
                    rules={[
                      {
                        required: true,
                        message: t("students.form.error.currency.required"),
                      },
                    ]}
                  >
                    <Select
                      defaultValue="dzd"
                      value={student?.payment?.currency}
                      onChange={(value) => {
                        handlePayment(value, student, "currency");
                        form?.setFieldValue(["currency", student?._id], value);
                      }}
                      style={{ width: 100, marginLeft: 10 }}
                    >
                      <Select.Option value="dzd">DZD</Select.Option>
                      <Select.Option value="eur">EUR</Select.Option>
                      <Select.Option value="usd">USD</Select.Option>
                    </Select>
                  </Form.Item>
                </Typography.Text>
              </List.Item>
            )}
          />
        </Form>
      </Modal>
      <Modal
        title={t("students.search")}
        open={isOpenSearch}
        centered
        onCancel={() => {
          setSearchInput("");
          setSelectedStudents([]);
          setIsOpenSearch(false);
        }}
        onOk={() => {
          setSearchInput("");
          setIsOpenSearch(false);
        }}
      >
        <Input
          type="text"
          onChange={handleSearchInput}
          prefix={<UserOutlined />}
          placeholder={t("students.table.column.fullName")}
        />
        <Divider orientation="left">{t("students.title")}</Divider>
        <List
          header={
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
              }}
            >
              <p>{t("students.table.column.fullName")}</p>
              <p>{t("students.table.column.email")}</p>
              <p></p>
            </div>
          }
          style={{ overflowY: "auto" }}
          bordered
          dataSource={filteredStudents}
          renderItem={(student) => (
            <List.Item
              onClick={() => {
                handleSelection(student);
              }}
              style={{
                cursor: "pointer",
                backgroundColor: `${
                  selectedStudents?.some(
                    (selectedStudent) => selectedStudent?._id === student?._id
                  )
                    ? "rgba(135, 206, 235, 0.6)"
                    : "inherit"
                }`,
              }}
            >
              <Typography.Text>{student?.fullName}</Typography.Text>
              <Typography.Text>{student?.email}</Typography.Text>
              {selectedStudents?.some(
                (selectedStudent) => selectedStudent?._id === student?._id
              ) ? (
                <CheckSquareOutlined />
              ) : (
                <BorderOutlined />
              )}
            </List.Item>
          )}
        />
      </Modal>
    </>
  );
};

export default AddStudentsToTrainingModal;
